/**
 * @fileoverview Global Offline Banner
 * 
 * Sprint 4O: Shows when user is offline with auto-retry messaging
 */

import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowReconnected(true);
        // Hide "reconnected" message after 3 seconds
        setTimeout(() => setShowReconnected(false), 3000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return { isOnline, showReconnected };
}

export function OfflineBanner() {
  const { isOnline, showReconnected } = useOnlineStatus();

  // Show "You're back online!" briefly
  if (showReconnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-green-500 text-white py-2 px-4 text-center text-sm font-medium shadow-lg animate-slide-down">
        <div className="flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4" />
          <span>You're back online! Syncing your data...</span>
        </div>
      </div>
    );
  }

  // Show offline banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium shadow-lg">
        <div className="flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>You're offline. We'll retry automatically when you're back online.</span>
        </div>
      </div>
    );
  }

  return null;
}

export default OfflineBanner;

